import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserStore } from "../../../store/user";
import styled from "styled-components";
import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { fonts } from "../../../styles/theme";
import {
  contarPorTipo,
  filtrarTermos,
  resumoDasBuscas,
  type FiltroDeTipo,
} from "../../dashboard/components/search-terms-growth/search-terms-summary";
import type { ISearchTerm, ISearchTermsGrowth } from "../../dashboard/types";
import { Cabecalho, Carregando, NaoEncontrado, Pe } from "../componentes";
import { NumeroAnimado } from "../componentes/numero-animado";
import { dataCurta, mesCurto, numero } from "../dados/formatar";
import { useRelatorio } from "../hooks/use-relatorio";
import {
  Apoio,
  Barra,
  Bloco,
  Card,
  Centro,
  Chip,
  Chips,
  Etiqueta,
  Faixa,
  Grade,
  Largura,
  Meta,
  Numero,
  Pagina,
  Pilula,
  Secao,
  Titulo,
  cores,
} from "../styles";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const JANELA = 3;
const PAGINA = 8;

const Coluna = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 16}px;
`;

const Busca = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid ${cores.line};
  border-radius: 1000px;
  padding: 12px 18px;
  background: #ffffff;

  input {
    border: 0;
    outline: 0;
    flex: 1;
    font: inherit;
    font-size: 14px;
    color: ${cores.ink};
    background: transparent;
    min-width: 0;
  }
`;

const Termo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px solid ${cores.line};

  .cabeca {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }
  .nome {
    font-size: 15px;
    overflow-wrap: anywhere;
  }
`;

const Alturas = styled.div`
  height: 180px;
  @media (min-width: 900px) {
    height: 280px;
  }
`;

const somaRecente = (valores: (number | null)[]) =>
  valores.slice(-JANELA).reduce<number>((soma, valor) => soma + (valor ?? 0), 0);

const pesquisasPeloNome = (dados: ISearchTermsGrowth): number[] =>
  dados.months.map((_, indice) =>
    dados.groups.filter((grupo) => grupo.kind === "BRAND").reduce((soma, grupo) => soma + (grupo.values[indice] ?? 0), 0),
  );

const opcoesDoGrafico: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { position: "bottom", labels: { boxWidth: 10, usePointStyle: true, font: { size: 13 } } },
    tooltip: { callbacks: { label: (contexto) => `${contexto.dataset.label}: ${contexto.parsed.y ?? 0} pesquisas` } },
  },
  scales: {
    y: { beginAtZero: true, ticks: { precision: 0, font: { size: 11 } }, grid: { color: cores.line } },
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
  },
  elements: { line: { tension: 0.35, borderWidth: 2.5 }, point: { radius: 3, hoverRadius: 5 } },
};

const rotuloDoTipo: Record<FiltroDeTipo, string> = {
  TODOS: "Todos",
  TOPIC: "Pelo serviço",
  BRAND: "Pelo nome",
  ADDRESS: "Pelo endereço",
};

const selo = (termo: ISearchTerm) => {
  if (termo.trend === "NEW") return <Etiqueta $cor="azul">Nova busca</Etiqueta>;
  if (termo.trend === "UP" && termo.growthPercent !== null && termo.growthPercent > 0) {
    return <Etiqueta $cor="verde">{`▲ ${Math.round(termo.growthPercent)}%`}</Etiqueta>;
  }
  return null;
};

export const AnalisePesquisas = () => {
  const { profileId = "" } = useParams<{ profileId: string }>();
  // Cliente logado volta para a tela dele (com a troca de empresa), não para o link público.
  const cliente = useUserStore((estado) => estado.user?.userType === "CUSTOMER");
  const voltarPara = cliente ? `/home?perfil=${profileId}` : `/relatorio/${profileId}`;
  const { pesquisas, carregando, carregandoPesquisas, falhou, perfil } = useRelatorio(profileId, 30);
  const [busca, setBusca] = useState("");
  const [tipo, setTipo] = useState<FiltroDeTipo>("TODOS");
  const [visiveis, setVisiveis] = useState(PAGINA);
  const hoje = useMemo(() => new Date(), []);

  const resumo = useMemo(() => (pesquisas && pesquisas.totalByMonth.length ? resumoDasBuscas(pesquisas) : null), [pesquisas]);
  const assuntos = useMemo(
    () =>
      (pesquisas?.groups ?? [])
        .filter((grupo) => grupo.kind === "TOPIC")
        .map((grupo) => ({ ...grupo, recente: somaRecente(grupo.values) }))
        .filter((grupo) => grupo.recente > 0)
        .sort((a, b) => b.recente - a.recente)
        .slice(0, 4),
    [pesquisas],
  );
  const termos = useMemo(
    () =>
      (pesquisas?.terms ?? [])
        .map((termo) => ({ termo, recente: somaRecente(termo.values), pouco: termo.values.slice(-JANELA).every((valor) => valor === null || valor === 0) }))
        .sort((a, b) => b.recente - a.recente),
    [pesquisas],
  );
  const filtrados = useMemo(
    () => filtrarTermos(termos.map((item) => item.termo), { busca, tipo }),
    [termos, busca, tipo],
  );
  const contagens = useMemo(() => contarPorTipo(termos.map((item) => item.termo)), [termos]);
  const comVolume = filtrados.filter((termo) => somaRecente(termo.values) > 0);
  const semVolume = filtrados.filter((termo) => somaRecente(termo.values) === 0);
  const maior = comVolume[0] ? somaRecente(comVolume[0].values) : 0;
  const grafico = useMemo(
    () =>
      pesquisas
        ? {
            labels: pesquisas.months.map(mesCurto),
            datasets: [
              { label: "Todas as pesquisas", data: pesquisas.totalByMonth, borderColor: cores.blue, backgroundColor: cores.blue },
              { label: "Pelo nome da empresa", data: pesquisasPeloNome(pesquisas), borderColor: cores.green, backgroundColor: cores.green },
            ],
          }
        : null,
    [pesquisas],
  );

  if (carregando || carregandoPesquisas) {
    return (
      <Pagina>
        <Cabecalho voltar={{ href: voltarPara, rotulo: "Voltar ao relatório" }} />
        <Carregando />
      </Pagina>
    );
  }
  if (falhou || !perfil) {
    return (
      <Pagina>
        <Cabecalho />
        <NaoEncontrado />
      </Pagina>
    );
  }

  const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  const ultimoMes = pesquisas?.months[pesquisas.months.length - 1];
  const mesSeguinte = (yearMonth: string) => {
    const [ano, mes] = yearMonth.split("-").map(Number);
    const data = new Date(ano, mes, 1);
    return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
  };
  const mesPendente = ultimoMes && ultimoMes < mesAtual ? mesCurto(mesSeguinte(ultimoMes)) : null;
  const limiar = pesquisas?.threshold ?? 15;
  const consultadoEm = dataCurta(hoje);

  return (
    <Pagina>
      <Cabecalho voltar={{ href: voltarPara, rotulo: "Voltar ao relatório" }} />

      <Largura>
        <Secao $atraso={0.15} style={{ paddingTop: 18, paddingBottom: 24 }}>
          <Centro>
            <Etiqueta>{perfil.profileName}</Etiqueta>
            <h1>O que as pessoas pesquisam para achar sua empresa</h1>
            <p className="frase" style={{ fontSize: 16, lineHeight: 1.6 }}>
              {`Termos reais que as pessoas digitaram no Google e fizeram sua empresa aparecer. Dados do próprio Google${resumo?.periodoRecente ? `, de ${resumo.periodoRecente}` : ""}.`}
            </p>
            {mesPendente && <Etiqueta $cor="amarela">{`${mesPendente} ainda não liberado pelo Google`}</Etiqueta>}
          </Centro>
        </Secao>
      </Largura>

      {resumo && resumo.totalRecente > 0 ? (
        <>
          <Largura>
            <Secao $atraso={0.3} style={{ paddingTop: 0, paddingBottom: 8 }}>
              <Bloco>
                <Card style={{ display: "flex", flexDirection: "column", gap: 6, justifyContent: "center" }}>
                  <Meta style={{ fontFamily: fonts.gilroySemiBold, color: cores.ink3 }}>{`Últimos ${JANELA} meses fechados`}</Meta>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                    <Numero $tamanho={56} $cor={cores.blue}>
                      <NumeroAnimado valor={resumo.totalRecente} atraso={0.4} />
                    </Numero>
                    <span style={{ fontSize: 17, color: cores.ink2 }}>pesquisas</span>
                  </div>
                  <Meta>{`mostraram sua empresa · ${resumo.periodoRecente}`}</Meta>
                </Card>
                {resumo.percentualPorNome !== null && (
                  <Grade $colunas={2}>
                    <Card style={{ display: "flex", flexDirection: "column", gap: 4, padding: "18px 20px" }}>
                      <Numero $tamanho={34} $cor={cores.green}>{`${100 - resumo.percentualPorNome}%`}</Numero>
                      <span style={{ fontSize: 13, fontFamily: fonts.gilroySemiBold }}>não citavam o nome</span>
                      <Meta>{`${numero(resumo.porServico + resumo.porEndereco)} pesquisas por serviço ou endereço`}</Meta>
                    </Card>
                    <Card style={{ display: "flex", flexDirection: "column", gap: 4, padding: "18px 20px" }}>
                      <Numero $tamanho={34}>{`${resumo.percentualPorNome}%`}</Numero>
                      <span style={{ fontSize: 13, fontFamily: fonts.gilroySemiBold }}>pelo nome da empresa</span>
                      <Meta>{`${numero(resumo.porNome)} de quem já conhece`}</Meta>
                    </Card>
                  </Grade>
                )}
              </Bloco>
            </Secao>
          </Largura>

          {grafico && (
            <Largura>
              <Secao $atraso={0.45}>
                <Coluna>
                  <Coluna $gap={6}>
                    <Titulo>Evolução mês a mês</Titulo>
                    <Apoio>Pesquisas por mês fechado · Dados do Google.</Apoio>
                  </Coluna>
                  <Alturas>
                    <Line data={grafico} options={opcoesDoGrafico} aria-label="Pesquisas por mês" />
                  </Alturas>
                </Coluna>
              </Secao>
            </Largura>
          )}

          {assuntos.length > 0 && (
            <Faixa $cor="azul" $atraso={0.6} style={{ background: cores.grayTint }}>
              <Largura>
                <Coluna>
                  <Coluna $gap={6}>
                    <Titulo>Os assuntos mais buscados</Titulo>
                    <Apoio>{`Pesquisas parecidas, agrupadas · últimos ${JANELA} meses.`}</Apoio>
                  </Coluna>
                  <Grade $colunas={1} $desktop={2}>
                    {assuntos.map((grupo) => (
                      <Card key={grupo.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "18px 22px" }}>
                        <Coluna $gap={2} style={{ minWidth: 0 }}>
                          <span style={{ fontSize: 15, fontFamily: fonts.gilroySemiBold, overflowWrap: "anywhere" }}>{grupo.name}</span>
                          <Meta>{`${grupo.termCount} ${grupo.termCount === 1 ? "forma" : "formas"} de buscar`}</Meta>
                        </Coluna>
                        <Numero $tamanho={30} $cor={cores.blue}>
                          <NumeroAnimado valor={grupo.recente} atraso={0.2} />
                        </Numero>
                      </Card>
                    ))}
                  </Grade>
                </Coluna>
              </Largura>
            </Faixa>
          )}

          <Largura>
            <Secao $atraso={0.75}>
              <Coluna>
                <Coluna $gap={6}>
                  <Titulo>Todos os termos</Titulo>
                  <Apoio>{`Total de ${resumo.periodoRecente} · as barras mostram a proporção.`}</Apoio>
                </Coluna>
                <Busca>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cores.ink4} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.3-4.3" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Buscar termo"
                    aria-label="Buscar termo"
                    value={busca}
                    onChange={(evento) => {
                      setBusca(evento.target.value);
                      setVisiveis(PAGINA);
                    }}
                  />
                </Busca>
                <Chips role="tablist" aria-label="Tipo de busca">
                  {(Object.keys(rotuloDoTipo) as FiltroDeTipo[])
                    .filter((chave) => chave === "TODOS" || contagens[chave] > 0)
                    .map((chave) => (
                      <Chip
                        key={chave}
                        type="button"
                        role="tab"
                        aria-selected={tipo === chave}
                        $ativo={tipo === chave}
                        onClick={() => {
                          setTipo(chave);
                          setVisiveis(PAGINA);
                        }}
                      >
                        {`${rotuloDoTipo[chave]} (${contagens[chave]})`}
                      </Chip>
                    ))}
                </Chips>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {comVolume.slice(0, visiveis).map((termo, indice) => {
                    const total = somaRecente(termo.values);
                    return (
                      <Termo key={termo.term}>
                        <div className="cabeca">
                          <span className="nome">
                            {termo.term}
                            {termo.kind === "BRAND" && <Meta> · pelo nome</Meta>}
                            {termo.kind === "ADDRESS" && <Meta> · pelo endereço</Meta>}
                            {selo(termo) && <span style={{ marginLeft: 6 }}>{selo(termo)}</span>}
                          </span>
                          <Numero $tamanho={17}>{numero(total)}</Numero>
                        </div>
                        <Barra $largura={maior ? (total / maior) * 100 : 0} $atraso={0.4 + Math.min(indice, 8) * 0.1} />
                      </Termo>
                    );
                  })}
                  {comVolume.length === 0 && <Meta>Nenhum termo encontrado com esse filtro.</Meta>}
                </div>
                {comVolume.length > visiveis && (
                  <Pilula as="button" type="button" $tipo="contorno" onClick={() => setVisiveis((valor) => valor + PAGINA)}>
                    {`Mostrar mais (${comVolume.length - visiveis} restantes)`}
                  </Pilula>
                )}
                {semVolume.length > 0 && (
                  <>
                    <Meta style={{ fontFamily: fonts.gilroySemiBold, letterSpacing: "0.06em", paddingTop: 6 }}>
                      {`COM MENOS DE ${limiar} BUSCAS POR MÊS`}
                    </Meta>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {semVolume.slice(0, visiveis).map((termo) => (
                        <div key={termo.term} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "11px 0", borderTop: `1px solid ${cores.line}` }}>
                          <span style={{ fontSize: 14, color: cores.ink3, overflowWrap: "anywhere" }}>
                            {termo.term}
                            {termo.kind === "BRAND" && <Meta> · pelo nome</Meta>}
                            {termo.kind === "ADDRESS" && <Meta> · pelo endereço</Meta>}
                          </span>
                          <Meta style={{ flexShrink: 0 }}>{`< ${limiar}`}</Meta>
                        </div>
                      ))}
                      {semVolume.length > visiveis && <Meta style={{ paddingTop: 8 }}>{`e mais ${semVolume.length - visiveis} termos com menos de ${limiar} buscas`}</Meta>}
                    </div>
                    <Meta>{`Termos com menos de ${limiar} buscas por mês aparecem como “< ${limiar}”: o Google não informa o número exato deles.`}</Meta>
                  </>
                )}
              </Coluna>
            </Secao>
          </Largura>
        </>
      ) : (
        <Largura>
          <Secao>
            <Card $fundo={cores.grayTint} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 15, fontFamily: fonts.gilroySemiBold }}>Como encontraram sua empresa</span>
              <Apoio style={{ margin: 0 }}>Ainda não há meses fechados suficientes para mostrar as pesquisas deste perfil.</Apoio>
              <Etiqueta $cor="amarela" style={{ alignSelf: "flex-start" }}>{`${mesCurto(mesAtual)} ainda não liberado pelo Google`}</Etiqueta>
            </Card>
          </Secao>
        </Largura>
      )}

      <Pe texto={`Dados do Google · consultado em ${consultadoEm}`} />
    </Pagina>
  );
};
